import type { Dispatch, RefObject, SetStateAction } from "react";
import { useEffect } from "react";
import type { DynastyRosterTeam, TradeAsset } from "../features/shared/dynastyTradingPageUtils";

type UseDynastyMobileUiArgs = {
  isNarrowViewport: boolean;
  mobileActiveTab: "teams" | "trade" | "find";
  mobileFindTradeMenuRef: RefObject<HTMLDivElement | null>;
  mobilePlayerLookupRef: RefObject<HTMLDivElement | null>;
  mobileTradeMeterRef: RefObject<HTMLDivElement | null>;
  mobileTwoTeamTradeMode: boolean;
  openTeams: DynastyRosterTeam[];
  previousMobileActiveTabRef: RefObject<"teams" | "trade" | "find">;
  selectedFindTradePlayer: { id: string; name: string } | null;
  selectedFindTradeTeam: DynastyRosterTeam | null;
  setActiveRookiePickerId: Dispatch<SetStateAction<string | null>>;
  setIsMobileFindTradeMenuOpen: Dispatch<SetStateAction<boolean>>;
  setIsPlayerLookupOpen: Dispatch<SetStateAction<boolean>>;
  setMobileActiveTab: Dispatch<SetStateAction<"teams" | "trade" | "find">>;
  setMobileFindTradeQuery: Dispatch<SetStateAction<string>>;
  setMobileTradeMeterHeight: Dispatch<SetStateAction<number>>;
  setOpenRosterIds: Dispatch<SetStateAction<number[]>>;
  setSelectionMode: Dispatch<SetStateAction<{ teamId: number } | null>>;
  setTradeSelections: Dispatch<SetStateAction<Record<number, Record<number, TradeAsset[]>>>>;
  tradeSelections: Record<number, Record<number, TradeAsset[]>>;
};

export function useDynastyMobileUi(args: UseDynastyMobileUiArgs) {
  const {
    isNarrowViewport,
    mobileActiveTab,
    mobileFindTradeMenuRef,
    mobilePlayerLookupRef,
    mobileTradeMeterRef,
    mobileTwoTeamTradeMode,
    openTeams,
    previousMobileActiveTabRef,
    selectedFindTradePlayer,
    selectedFindTradeTeam,
    setActiveRookiePickerId,
    setIsMobileFindTradeMenuOpen,
    setIsPlayerLookupOpen,
    setMobileActiveTab,
    setMobileFindTradeQuery,
    setMobileTradeMeterHeight,
    setOpenRosterIds,
    setSelectionMode,
    setTradeSelections,
    tradeSelections,
  } = args;

  useEffect(() => {
    if (!isNarrowViewport) return;
    setOpenRosterIds((prev) => (prev.length <= 2 ? prev : prev.slice(0, 2)));
  }, [isNarrowViewport, setOpenRosterIds]);

  useEffect(() => {
    if (!isNarrowViewport) return;
    if (mobileActiveTab === "trade" && openTeams.length !== 2) {
      setMobileActiveTab("teams");
    }
  }, [isNarrowViewport, mobileActiveTab, openTeams.length, setMobileActiveTab]);

  useEffect(() => {
    if (!isNarrowViewport) return;
    const previousTab = previousMobileActiveTabRef.current;
    if (previousTab === "trade" && mobileActiveTab !== "trade") {
      setTradeSelections({});
      setSelectionMode(null);
      setActiveRookiePickerId(null);
    }
    previousMobileActiveTabRef.current = mobileActiveTab;
  }, [
    isNarrowViewport,
    mobileActiveTab,
    previousMobileActiveTabRef,
    setActiveRookiePickerId,
    setSelectionMode,
    setTradeSelections,
  ]);

  useEffect(() => {
    if (!isNarrowViewport) return;
    if (!selectedFindTradePlayer) {
      setMobileFindTradeQuery("");
      return;
    }
    if (selectedFindTradeTeam?.players.some((player) => player.id === selectedFindTradePlayer.id)) {
      setMobileFindTradeQuery(selectedFindTradePlayer.name);
    }
  }, [isNarrowViewport, selectedFindTradePlayer, selectedFindTradeTeam, setMobileFindTradeQuery]);

  useEffect(() => {
    if (!isNarrowViewport) return undefined;
    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (mobilePlayerLookupRef.current && target && !mobilePlayerLookupRef.current.contains(target)) {
        setIsPlayerLookupOpen(false);
      }
      if (mobileFindTradeMenuRef.current && target && !mobileFindTradeMenuRef.current.contains(target)) {
        setIsMobileFindTradeMenuOpen(false);
      }
    };
    document.addEventListener("click", handleDocumentClick);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, [
    isNarrowViewport,
    mobileFindTradeMenuRef,
    mobilePlayerLookupRef,
    setIsMobileFindTradeMenuOpen,
    setIsPlayerLookupOpen,
  ]);

  useEffect(() => {
    if (!mobileTwoTeamTradeMode) return undefined;
    const element = mobileTradeMeterRef.current;
    if (!element) return undefined;

    const updateHeight = () => {
      const nextHeight = Math.ceil(element.getBoundingClientRect().height);
      if (nextHeight > 0) {
        setMobileTradeMeterHeight(nextHeight);
      }
    };

    updateHeight();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateHeight);
      window.addEventListener("orientationchange", updateHeight);
      return () => {
        window.removeEventListener("resize", updateHeight);
        window.removeEventListener("orientationchange", updateHeight);
      };
    }

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(element);
    window.addEventListener("resize", updateHeight);
    window.addEventListener("orientationchange", updateHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateHeight);
      window.removeEventListener("orientationchange", updateHeight);
    };
  }, [mobileTradeMeterRef, mobileTwoTeamTradeMode, setMobileTradeMeterHeight, tradeSelections]);
}
